import React, { Fragment, useEffect, useMemo, useState } from "react";
import { colors2, getVal } from 'oolib';
import { StickyHorizontalScrollbar } from "../../generalUI/StickyHorizontalScrollbar";
import TableHeaderElem from "./comps/TableHeaderElem";
import TableRowElem from "./comps/TableRowElem";
import { TableSkeletonLoader } from "./comps/TableSkeletonLoader";
import { useParseTplToTableConfig } from "./CONFIG_PARSER/useParseTplToTableConfig";
import { StyledDataTableRowsContainer } from "./styled";
import { filterOutHiddenCols } from "./utils/filterOutHiddenCols";
import { sortCellDataAsPerOrderConfig } from "./utils/sortCellDataAsPerOrderConfig";
import { useHandleTableColWidths } from "./utils/useHandleTableColWidths";
import { useTableSort } from "./utils/tableSortUtils/useTableSort";
import { ButtonTertiary } from "oolib/dist/v2/components/Buttons";
import { useGetBulkActionsConfig } from "./utils/useGetBulkActionsConfig";

export const ROW_HEIGHT = 36

function TableElem({
  id,
  status,
  data: dataProp = [],
  
  tableConfig: tableConfigProp,
  actionsConfig,
  conditionsConfig,
  handleRowClick,
  TableWrapper = "div",
  
  lazyLoad: {
    observerRef,
    isFetchingNextPage,
    backendActiveSort,
    setBackendActiveSort,
  } = {},

  //builder props
  builderProps,

  handleCellChange,
  //for future
  addRow,
  enableContainerQuery

}){
  // const props = arguments[0]
  // useTraceUpdateOnLocal(props);  

  const { tableConfig } = useParseTplToTableConfig({ 
    tableConfigProp,
    isBackendSort : !!backendActiveSort 
  });

  /**
   * We feel that it might be more 'correct' to have the sidebar controlling hide/show & column order, 
   * within the table comp itself (rather than outsourcing to ContentTypeConfigDashboard). 
   * 
   * If we do make that change, then the below useEffect will become irrelevant
   * this is a bit problematic, cuz it led to infinite loop 
   * when sortCellData was done in useParseTplToTable...
   */
  useEffect(() => {
    builderProps?.trackParsedTableConfig && 
    builderProps.trackParsedTableConfig(
      sortCellDataAsPerOrderConfig(tableConfig)
    )
  },[tableConfig])
  
  /**
   * having this as a state here can be used for
   * giving user control over col widths in the future
   */
  const { colWidthConfig, setColWidthConfig } = useHandleTableColWidths({ tableConfig });

  //sort related stuff
  const { data, activeSorts, handleTableSort } = useTableSort({
    tableConfig,
    dataProp,
    backendActiveSort,
    setBackendActiveSort,
  });

  const {
    bulkActions_selectedRowIds,
    bulkActions_setSelectedRowIds,
    bulkActionsConfig,
    ActivateSelfServeSurveyModal,
  } = useGetBulkActionsConfig({ contentType: data?.[0]?.meta?.kp_content_type, tableConfig });

  /**
   * we need to do this tableRowIds setting here
   * just 1 single time in the components lifecycle
   * because, if the idpath field happens to be editable,
   * it can lead to all sorts of strange bugs if the row id
   * keeps changing during edit.
   */
  const tableRowIds = useMemo(() => {
    if(status === undefined || status === "success"){
      const allIds = data.map(d => {
        return getVal(d, tableConfig.rowIdPath);
      });

      bulkActions_setSelectedRowIds(prevSelected => prevSelected.filter(id => allIds.includes(id)));

      return allIds;
    }
  },[status, data.length])

  const bulkActions = tableConfig.bulkActions;

  return (
    <StickyHorizontalScrollbar>
      <div style={{ width: "fit-content" }}>
      <TableWrapper enableContainerQuery={enableContainerQuery}>
      {ActivateSelfServeSurveyModal}
      {bulkActions && (
        <div
          style={{
            paddingTop: "1px",
            paddingBottom: "6px",
            paddingLeft: "1px",
            display: "flex",
            gap: "4px",
          }}
        >
          {
            bulkActions.map((actionObj) => (
              <ButtonTertiary
                color={colors2.grey80}
                disabled={bulkActions_selectedRowIds.length === 0}
                style={{
                  borderRadius: "14px",
                  paddingBlock: "4px",
                  paddingInline: "10px"
                }}
                onClick={() => bulkActionsConfig[actionObj.action]({ data })}
              >
                {actionObj.display}
              </ButtonTertiary>
            ))
          }
        </div>
      )}
        <TableHeaderElem
          id={id + "_header"}
          tableConfig={filterOutHiddenCols(sortCellDataAsPerOrderConfig(tableConfig))}
          handleTableSort={handleTableSort}
          activeSorts={activeSorts}
          actions={tableConfig.actions}
          bulkActions={tableConfig.bulkActions}
          colWidthConfig={colWidthConfig}
          setColWidthConfig={setColWidthConfig}
          backendActiveSort={backendActiveSort}
          builderProps={builderProps}
          tableRowIds={tableRowIds}
          bulkActions_selectedRowIds={bulkActions_selectedRowIds}
          bulkActions_setSelectedRowIds={bulkActions_setSelectedRowIds}
        />
        <StyledDataTableRowsContainer style={{ borderRight: !builderProps && !tableConfig.actions && 'none'}}>
          {status !== undefined && status === "loading" ? (
            <TableSkeletonLoader rowsCount={10} />
          ) : (
            data.map((d, i) => {
              let rowId = tableRowIds[i] //getVal(d, tableConfig.rowIdPath);
              return (
                <Fragment key={rowId}>
                <TableRowElem
                  key={rowId}
                  tableConfig={filterOutHiddenCols(sortCellDataAsPerOrderConfig(tableConfig))}
                  id={rowId}
                  data={d}
                  rowIdx={i}
                  actions={tableConfig.actions}
                  bulkActions={tableConfig.bulkActions}
                  actionsConfig={actionsConfig}
                  conditionsConfig={conditionsConfig}
                  colWidthConfig={colWidthConfig}
                  handleRowClick={handleRowClick}
                  handleCellChange={handleCellChange}
                  bulkActions_selectedRowIds={bulkActions_selectedRowIds}
                  bulkActions_setSelectedRowIds={bulkActions_setSelectedRowIds}
                />
                { i === data.length - 1 &&
                  <div 
                    ref={observerRef} 
                    id={'observerAttached_'+ d._id + '_' + data.length }
                    /**
                     * it is extremely important that this id changes everytime a new set is rendered
                     * because, if the last item in the newly rendered set continues to be 'inView', then
                     * useElemInView checks to see if the id has changed. if it has, then it will make the fetch
                     * request for the next set of data.
                     */
                    />}
                </Fragment>
              );
            })
          )}

          {isFetchingNextPage && (
            <TableSkeletonLoader rowsCount={5} dataLength={data?.length || 0} />
          )}
          
        </StyledDataTableRowsContainer>
      </TableWrapper>
      </div>
    </StickyHorizontalScrollbar>
  )
};

export default TableElem;
